import { useState } from 'react';
import { Button, Modal, Select } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useAddMember } from '@/hooks/useMembers';
import { UserPicker } from './UserPicker';
import type { SpaceMembership, UserSummary } from '@/types/spaces';

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  spaceId: string;
  /** Existing memberships — used to filter out users who are already in. */
  existingMembers: SpaceMembership[];
}

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member — can read + write entities in this space' },
  { value: 'admin', label: 'Admin — member powers + manage other members' },
  { value: 'viewer', label: 'Viewer — read-only access' },
];

export function AddMemberModal({ isOpen, onClose, spaceId, existingMembers }: AddMemberModalProps) {
  const { success, error: showError } = useToast();
  const addMember = useAddMember(spaceId);
  const [user, setUser] = useState<UserSummary | undefined>(undefined);
  const [role, setRole] = useState<'admin' | 'member' | 'viewer'>('member');

  const exclude = new Set(existingMembers.map((m) => m.userId));

  const reset = () => {
    setUser(undefined);
    setRole('member');
  };

  const handleClose = () => {
    if (addMember.isPending) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!user) return;
    try {
      await addMember.mutateAsync({ userId: user.userId, role });
      success(`Added ${user.email} as ${role}`);
      reset();
      onClose();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to add member');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Member"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={addMember.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={addMember.isPending} disabled={!user}>
            Add Member
          </Button>
        </>
      }
    >
      <div className="space-y-4 py-2">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">User</label>
          <UserPicker
            value={user?.userId}
            onChange={setUser}
            excludeUserIds={exclude}
            placeholder="Search by email or name…"
          />
          <p className="text-xs text-muted-foreground">
            Existing members are hidden. To change a member's role, edit their row in the members table.
          </p>
        </div>

        <Select
          label="Role"
          options={ROLE_OPTIONS}
          value={role}
          onChange={(e: { target: { value: string } }) =>
            setRole(e.target.value as 'admin' | 'member' | 'viewer')
          }
        />
      </div>
    </Modal>
  );
}
